import React from 'react';
import { Card, Col, Row, Tag, Typography, theme } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useOpportunityPipeline } from '@/hooks/useOpportunities';
import type { PipelineStageRow } from '@/services/opportunity.service';

import { OPPORTUNITY_STAGES, canonicalOpportunityStage } from '@/constants/opportunity-stages';

const { Text } = Typography;

function asFiniteNumber(value: unknown): number {
  const n = Number(value ?? 0);
  return Number.isFinite(n) ? n : 0;
}

export const OpportunitiesKanbanBoard: React.FC = () => {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const { data: pipeline = [], isLoading } = useOpportunityPipeline();

  const byStage = React.useMemo(() => {
    const map = new Map<string, PipelineStageRow>();
    for (const s of OPPORTUNITY_STAGES) {
      map.set(s, { stage: s, count: 0, total_value: 0, expected_revenue: 0, opportunities: [] });
    }
    for (const row of pipeline) {
      const stage = canonicalOpportunityStage(row.stage);
      const existing = map.get(stage);
      if (!existing) {
        map.set(stage, { ...row, stage, opportunities: row.opportunities ?? [] });
        continue;
      }
      existing.count = asFiniteNumber(existing.count) + asFiniteNumber(row.count);
      existing.total_value = asFiniteNumber(existing.total_value) + asFiniteNumber(row.total_value);
      existing.expected_revenue =
        asFiniteNumber(existing.expected_revenue) + asFiniteNumber(row.expected_revenue);
      existing.opportunities = [...(existing.opportunities ?? []), ...(row.opportunities ?? [])];
    }
    return OPPORTUNITY_STAGES.map((s) => map.get(s)!);
  }, [pipeline]);

  return (
    <Row gutter={12} wrap={false} style={{ overflowX: 'auto', paddingBottom: 8 }}>
      {byStage.map((col) => (
        <Col key={col.stage} flex="0 0 260px">
          <Card
            size="small"
            title={
              <span>
                {col.stage}{' '}
                <Tag>{col.count ?? 0}</Tag>
              </span>
            }
            loading={isLoading}
            style={{ background: token.colorFillAlter, minHeight: 320 }}
          >
            {(col.opportunities ?? []).length === 0 ? (
              <Text type="secondary">No opportunities</Text>
            ) : (
              (col.opportunities ?? []).map((o) => (
                <Card
                  key={o.id}
                  size="small"
                  hoverable
                  style={{ marginBottom: 8, cursor: 'pointer' }}
                  onClick={() => navigate(`/growth/opportunities/${o.id}`)}
                >
                  <Text strong>{o.opportunity_name}</Text>
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {o.opportunity_number} · {o.customer_name}
                    </Text>
                  </div>
                  {o.estimated_value != null && (
                    <div>
                      <Text style={{ fontSize: 12 }}>{Number(o.estimated_value).toLocaleString()}</Text>
                    </div>
                  )}
                </Card>
              ))
            )}
          </Card>
        </Col>
      ))}
    </Row>
  );
};
